// /home/Hakyury/sites/eight-percent.com/frontend/src/component/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getTokens,
  extractTokensFromURL,
  getOAuthRedirectUrl,
  getOAuthLogoutUrl,
  clearTokens
} from '../utils/authFetch';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Проверяем авторизацию при загрузке
  useEffect(() => {
    const initialize = () => {
      setLoading(true);
      
      // Извлекаем токены из URL если есть
      extractTokensFromURL();
      
      // Проверяем токены
      checkAuth();
      
      setLoading(false);
    };
    
    initialize();
    
    // Слушаем сообщения от OAuth окна
    const handleMessage = (event) => {
      if (event.origin !== 'https://oauth.eight-percent.com') return;
      
      if (event.data.type === 'oauth_success') {
        const { access_token, refresh_token } = event.data;
        localStorage.setItem('eight_percent_access_token', access_token);
        localStorage.setItem('eight_percent_refresh_token', refresh_token);
        checkAuth();
      }
    };
    
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      
      const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scrolled = height > 0 ? (winScroll / height) * 100 : 0;
      setScrollProgress(scrolled);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Проверяем авторизацию
  const checkAuth = () => {
    const tokens = getTokens();
    
    if (!tokens.access) {
      setUser(null);
      setIsAdmin(false);
      return;
    }

    try {
      // Декодируем JWT токен
      const payload = JSON.parse(atob(tokens.access.split('.')[1]));
      setUser({
        username: payload.login || payload.username,
        role: payload.role
      });
      setIsAdmin(payload.role === 'Администратор');
    } catch (error) {
      console.error('Error decoding token:', error);
      setUser(null);
      setIsAdmin(false);
    }
  };

  // Выход из системы
  const handleLogout = () => {
    clearTokens();
    setUser(null);
    setIsAdmin(false);
    setIsProfileMenuOpen(false);
    // OAuth хранит токены в своём localStorage, поэтому очищаем и его сессию.
    window.location.replace(getOAuthLogoutUrl('https://eight-percent.com/'));
  };

  if (loading) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center border border-white/20">
                <span className="text-white font-bold text-sm">8%</span>
              </div>
            </div>
            <div className="animate-pulse bg-gray-700/50 h-8 w-20 rounded"></div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header 
      className={`
        fixed top-0 left-0 right-0 z-50 transition-all duration-300
        ${scrolled 
          ? 'bg-black/80 backdrop-blur-xl' 
          : 'bg-gradient-to-b from-black/90 via-black/50 to-transparent'
        }
      `}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Логотип */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center border border-white/20">
                <span className="text-white font-bold text-sm">8%</span>
              </div>
              <div className="absolute -inset-2 border border-purple-400/20 rounded-full animate-ping"></div>
            </div>
          </Link>

          {/* Десктопная навигация */}
          <nav className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-white/5 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center">
                    <span className="text-white font-bold text-xs">
                      {user.username?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="text-white text-sm font-medium">{user.username}</p>
                    <p className="text-gray-400 text-xs">{user.role}</p>
                  </div>
                </button>
                
                {/* Выпадающее меню профиля */}
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
                    <div className="p-4 border-b border-white/10">
                      <p className="text-white font-medium truncate">{user.username}</p>
                      <p className="text-gray-400 text-sm">{user.role}</p>
                    </div>
                    
                    <div className="py-2">
                      <a
                        href="https://profile.eight-percent.com"
                        className="flex items-center space-x-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span>Профиль</span>
                      </a>
                      
                      {isAdmin && (
                        <a
                          href="https://admin.eight-percent.com"
                          className="flex items-center space-x-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                          onClick={() => setIsProfileMenuOpen(false)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Админ</span>
                        </a>
                      )}
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 w-full px-4 py-3 text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors border-t border-white/10"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Выход</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a
                href={getOAuthRedirectUrl()}
                className="group relative px-6 py-2.5 rounded-lg overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/80 to-indigo-600/80 opacity-90 group-hover:opacity-100 transition-opacity"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 to-cyan-400 rounded-lg blur opacity-0 group-hover:opacity-20 transition-opacity"></div>
                <span className="relative text-white font-medium">
                  Войти
                </span>
              </a>
            )}
          </nav>

          {/* Мобильное меню */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
              aria-label="Меню"
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <div className="space-y-1.5">
                  <div className="w-6 h-0.5 bg-gray-300"></div>
                  <div className="w-6 h-0.5 bg-gray-300"></div>
                  <div className="w-6 h-0.5 bg-gray-300"></div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Мобильное выпадающее меню */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-4 right-4 bg-black/90 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 space-y-4">
              {user ? (
                <>
                  <div className="flex items-center space-x-3 p-3 bg-gray-800/30 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        {user.username?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="text-white font-medium">{user.username}</p>
                      <p className="text-gray-400 text-sm">{user.role}</p>
                    </div>
                  </div>
                  
                  <a
                    href="https://profile.eight-percent.com"
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors rounded-lg"
                  >
                    Профиль
                  </a>
                  
                  {isAdmin && (
                  <a
                    href={`https://oauth.eight-percent.com?next=${encodeURIComponent('https://admin.eight-percent.com')}`}
                    onClick={() => setIsMenuOpen(false)}
                      className="block w-full px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors rounded-lg"
                    >
                      Админ
                    </a>
                  )}
                  
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-3 text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors rounded-lg border-t border-white/10 mt-4"
                  >
                    Выход
                  </button>
                </>
              ) : (
                <a
                  href={getOAuthRedirectUrl()}
                  onClick={() => setIsMenuOpen(false)}
                  className="block w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-3 rounded-lg font-medium text-center"
                >
                  Войти
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Анимированная полоса прогресса */}
      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent">
        <div 
          className="h-full bg-gradient-to-r from-purple-400 to-cyan-400 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>
    </header>
  );
};

export default Header;
